// Create / edit dialog for a team (see `features/teams-sports.md`). A team is
// a (company, sport) pair with a name: the sport is chosen from the live
// catalog at creation and fixed for the team's lifetime (memberships depend
// on it), so when editing only the name is open. Mutations respond with the
// company's refreshed { teams, memberships } snapshot, handed to `onSaved`.

import { useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import type { Sport, Team } from '../domain';
import { TEAM_NAME_MAX, validateTeam } from '../domain';
import type { TeamsSnapshot } from '../data/apiClient';
import { createTeam, updateTeam } from '../data/apiClient';
import { useTranslation } from '../state/I18nContext';

interface TeamDialogProps {
  open: boolean;
  /** The company a team created here belongs to. */
  companyId: number;
  /** The team being renamed, or null to create a new one. */
  team: Team | null;
  /** The live sport catalog (labels and the create-time sport choice). */
  sports: Sport[];
  onClose: () => void;
  onSaved: (snapshot: TeamsSnapshot) => void;
}

export function TeamDialog({
  open,
  companyId,
  team,
  sports,
  onClose,
  onSaved,
}: TeamDialogProps) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  // '' until picked (creates start without a preselected sport).
  const [sportId, setSportId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // (Re)seed the form whenever the dialog opens for a different target.
  useEffect(() => {
    if (!open) return;
    setName(team?.name ?? '');
    setSportId(team?.sportId ?? '');
    setError(null);
  }, [open, team]);

  // Prefer the locale catalogue entry for a sport name; sports missing from
  // the catalogues fall back to the server-provided English name.
  const sportLabel = (sport: Sport) => {
    const translated = t(`sports.${sport.id}`);
    return translated === `sports.${sport.id}` ? sport.name : translated;
  };

  const validation = useMemo(
    () =>
      validateTeam(
        { name, sportId },
        sports.map((sport) => sport.id),
      ),
    [name, sportId, sports],
  );

  const submit = async () => {
    if (!validation.valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const snapshot = team
        ? await updateTeam(team.id, { name })
        : await createTeam(companyId, { name, sportId });
      onSaved(snapshot);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.teams.errors.save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {team
          ? t('admin.teams.dialog.editTitle', { name: team.name })
          : t('admin.teams.dialog.createTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label={t('admin.teams.dialog.name')}
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: TEAM_NAME_MAX } }}
          />
          <TextField
            select
            label={t('admin.teams.dialog.sport')}
            value={sportId}
            onChange={(e) => setSportId(e.target.value)}
            disabled={team !== null}
            helperText={team ? t('admin.teams.dialog.sportLocked') : undefined}
          >
            {sports.map((sport) => (
              <MenuItem key={sport.id} value={sport.id}>
                {sportLabel(sport)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('admin.common.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !validation.valid}
          onClick={() => void submit()}
        >
          {t('admin.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
